import { useContext, useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import useThemeDetector from "hooks/useThemeDetector";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import SoftAvatar from "components/SoftAvatar";
import DefaultUserAvatar from "layouts/pages/account/components/Header/components/DefaultUserAvatar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import IconButton from "@mui/material/IconButton";
import { navbarIconButton } from "cxfcomponents/Navbars/DashboardNavbar/styles";
import ProtectedButton from "authentication/ProtectedButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {
	SettingsProfileIcon,
	SettingsThemeIcon,
	SettingsLightThemeIcon,
	SettingsDarkThemeIcon,
	SettingsSystemThemeIcon,
	SettingsLogoutIcon
} from "../../../../../assets/CXFIcons/Portal/SettingsMenu";
import { ThemedAngleLeft, ThemedAngleRight } from "assets/CXFIcons/Arrows";
import { ThemedHelpIcon } from "assets/CXFIcons/Portal/Navigation";
import { THEME_TYPES } from "constants/theme";
import { UserContext } from "context/AppProvider/UserContext";
import {
	setThemeMode,
	setIsSystemTheme
} from "redux/actions/portal/settingsActionCreator";
import useLogout from "hooks/useLogout";
import { useRoles } from "hooks/useRoles";
import "./style.css";
import { PERMISSIONS } from "constants/portal";
import { executorSocket } from "layouts/Flow Designer/components/Sockets";
import UsersCell from "layouts/Flows/components/UsersCell";
import { reloadFlow } from "redux/actions/flowDesigner/flowsActionCreator";
import { useAuth0 } from "@auth0/auth0-react";
import { fetchFlowById } from "redux/actions/flowDesigner/flowsActionCreator";
import { getEnv } from "helpers/environment";
import { canParseJSON } from "helpers/common";
import { updateUserPreferences } from "api/UserApi";
import { setUserPreferences } from "redux/actions/portal/usersActionCreator";

const SettingsMenu = () => {
	const { themeMode, isSystemTheme } = useSelector(
		(store) => store?.portal?.settings
	);
	const [openSettings, setOpenSettings] = useState(false);
	const [openSubMenu, setOpenSubMenu] = useState(false);
	const [settingsMenuLoading, setSettingsMenuLoading] = useState(false);
	const { userData } = useContext(UserContext);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isDarkTheme = useThemeDetector();
	const { pathname } = useLocation();
	const { portalLogout } = useLogout();
	const [isLoggingOut, setIsLoggingOut] = useState(false);
	const { roles } = useRoles(false);
	const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
	const [latestFlowUpdate, setLatestFlowUpdate] = useState(null);
	const [usersJoined, setUsersJoined] = useState([]);
	const hasStartedListening = useRef(false);
	const { user } = useAuth0();

	const handleDropDown = (event) => {
		setOpenSubMenu(event.currentTarget);
	};

	const handleCloseSubMenu = () => {
		setOpenSubMenu(false);
	};

	const handleOpenSettings = (event) => {
		setOpenSettings(event.currentTarget);
		setSettingsMenuLoading(true);
	};

	const handleCloseSettings = () => {
		setSettingsMenuLoading(false);
		setOpenSettings(false);
		setOpenSubMenu(false);
	};

	const handleGoToSettings = () => {
		setOpenSettings(false);
		if (!pathname.includes("/designer")) {
			navigate("/home/profile");
		} else {
			window.open("/home/profile", "_blank");
		}
	};

	const handleGoToHelp = () => {
		setOpenSettings(false);
		window.open(
			"https://cxfabric.io/docs/#/introduction/what-is-cxfabric",
			"_blank"
		);
	};
	const handleLogout = () => {
		setIsLoggingOut(true);
		portalLogout();
	};

	const changeTheme = async (theme) => {
		const isSystem = theme === THEME_TYPES.SYSTEM;
		let userPreferences = canParseJSON(userData?.user_preferences)
			? JSON.parse(userData?.user_preferences)
			: {};
		userPreferences = {
			...userPreferences,
			user_preferences: {
				...userPreferences?.user_preferences,
				portal: { themeMode: { isSystem: isSystem, theme: theme } }
			}
		};

		const updateUserPreferencesResponse = await updateUserPreferences(
			user?.cxfUserId,
			user?.stripeId,
			userPreferences
		);

		dispatch(setUserPreferences(updateUserPreferencesResponse));

		dispatch(setIsSystemTheme(isSystem));

		if (isSystem) {
			dispatch(
				setThemeMode(isDarkTheme ? THEME_TYPES.DARK : THEME_TYPES.LIGHT)
			);
		} else {
			dispatch(setThemeMode(theme));
		}
		handleCloseSettings();
	};

	const renderThemeSettingsMenu = () => {
		return (
			<Menu
				className="theme-settings-menu"
				anchorEl={openSubMenu}
				anchorOrigin={{ horizontal: "left", vertical: "top" }}
				transformOrigin={{ horizontal: "right", vertical: "top" }}
				open={Boolean(openSubMenu)}
				onClose={handleCloseSubMenu}
				sx={{ ml: -1.5 }}
			>
				<MenuList>
					<MenuItem
						selected={themeMode === THEME_TYPES.LIGHT && !isSystemTheme}
						onClick={() => changeTheme(THEME_TYPES.LIGHT)}
					>
						<ListItemIcon>
							<SettingsLightThemeIcon />
						</ListItemIcon>
						Light
					</MenuItem>
					<MenuItem
						selected={themeMode === THEME_TYPES.DARK && !isSystemTheme}
						onClick={() => changeTheme(THEME_TYPES.DARK)}
					>
						<ListItemIcon>
							<SettingsDarkThemeIcon />
						</ListItemIcon>
						Dark
					</MenuItem>
					<MenuItem
						selected={isSystemTheme}
						onClick={() => changeTheme(THEME_TYPES.SYSTEM)}
					>
						<ListItemIcon>
							<SettingsSystemThemeIcon />
						</ListItemIcon>
						System
					</MenuItem>
				</MenuList>
			</Menu>
		);
	};

	const getJoinedUsers = () => {
		return usersJoined.filter((user) => user.auth0_id !== userData.auth0_id);
	};

	const getLatestUpdateFormat = (date) => {
		const options = {
			hour: "numeric",
			minute: "2-digit",
			hour12: true
		};

		return new Date(date).toLocaleTimeString("en-US", options).toLowerCase();
	};

	const handleRefreshFlow = () => {
		setLatestFlowUpdate(null);
		dispatch(
			fetchFlowById(
				user?.stripeId,
				currentSavedFlow.id,
				currentSavedFlow.current_version
			)
		);
		dispatch(reloadFlow());
	};

	useEffect(() => {
		const handleStorageChange = (event) => {
			if (event.key === "realtimeAvatar") {
				userData.image = event.newValue;
			}
		};

		window.addEventListener("storage", handleStorageChange);

		return () => {
			window.removeEventListener("storage", handleStorageChange);
		};
	}, []);

	useEffect(() => {
		if (hasStartedListening.current) return;

		if (!executorSocket) return;

		hasStartedListening.current = true;

		executorSocket.on("flowRoomUsers", (payload) => {
			if (payload) setUsersJoined(Object.values(payload).filter((user) => user.auth0_id !== userData.auth0_id));
		}).on("leaveFlowRoom", (payload) => {
			setUsersJoined((prev) => prev.filter((user) => user.auth0_id !== payload.userId));
		}).on("flowRoomUpdate", (payload) => {
			setLatestFlowUpdate(payload);
		});
	}, [executorSocket]);

	useEffect(() => {
		const faviconPath =
			themeMode === THEME_TYPES.LIGHT
				? "/favicon-light.png"
				: "favicon-dark.png";
		let link = document.querySelector("link[rel~='icon']");

		if (!link) {
			link = document.createElement("link");
			link.rel = "icon";
			document.head.appendChild(link);
		}
		link.href = faviconPath;
	}, [themeMode]);

	return (
		<SoftBox sx={{ display: "flex", alignItems: "center" }}>
			{latestFlowUpdate &&
				latestFlowUpdate.flowId === currentSavedFlow?.id && (
					<SoftTypography sx={{ fontSize: "13px", marginRight: "8px" }}>
						New update from {getLatestUpdateFormat(latestFlowUpdate.date)}
						.
						<button
							style={{
								textDecoration: "underline",
								marginLeft: "2px",
								background: "none",
								border: "none",
								padding: 0,
								cursor: "pointer"
							}}
							onClick={handleRefreshFlow}
						>
							Refresh flow
						</button>
					</SoftTypography>
				)}
			{usersJoined.length !== 0 && <UsersCell users={getJoinedUsers()} />}
			<IconButton
				sx={navbarIconButton}
				size="small"
				onClick={handleOpenSettings}
			>
				{userData?.image && userData?.image.trim() ? (
					<SoftAvatar
						src={`${userData?.image}`}
						alt="profile-image"
						variant="full"
						size="xs"
					/>
				) : (
					<SoftAvatar
						alt="profile-image"
						variant="full"
						size="xs"
					>
						<DefaultUserAvatar
							firstName={userData?.first_name}
							lastName={userData?.last_name}
							open={false}
							size="xxs"
							border="unset"
						/>
					</SoftAvatar>
				)}
			</IconButton>
			<Menu
				anchorEl={openSettings}
				anchorReference={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left"
				}}
				open={Boolean(openSettings)}
				onClose={handleCloseSettings}
				sx={{ mt: -0.5, ml: -1.5 }}
			>
				<MenuList>
					<ProtectedButton
						permissionName={PERMISSIONS.ACCESS_TO_ACCOUNT_SETTINGS}
						setSettingsMenuLoading={setSettingsMenuLoading}
					>
						<Grid
							container
							spacing={1}
							alignItems="center"
						>
							<Grid>
								{userData?.image && userData?.image.trim() ? (
									<SoftAvatar
										src={`${userData?.image}`}
										alt="profile-image"
										variant="full"
										size="lg"
									/>
								) : (
									<DefaultUserAvatar
										firstName={userData?.first_name}
										lastName={userData?.last_name}
										open={false}
										size="lg"
										border="unset"
									/>
								)}
							</Grid>
							<Grid>
								<SoftBox
									height="100%"
									mt={0.5}
									lineHeight={1}
								>
									<SoftTypography
										variant="h5"
										fontWeight="light"
									>
										{userData
											? userData?.first_name +
												" " +
												userData?.last_name
											: ""}
									</SoftTypography>
									<SoftTypography
										variant="button"
										color="text"
										fontWeight="light"
									>
										{userData ? userData?.email_address : ""}
									</SoftTypography>
								</SoftBox>
								<SoftTypography
									variant="button"
									color="text"
									fontWeight="light"
								>
									{roles?.find((role) => role.id === userData?.role_id)
										?.name || ""}
								</SoftTypography>
							</Grid>
						</Grid>

						<Divider />

						<MenuItem onClick={handleGoToSettings}>
							<ListItemIcon>
								<SettingsProfileIcon />
							</ListItemIcon>
							Profile
						</MenuItem>
						{getEnv("IS_COMPONENT_DOWN_HELP_MENU") === "active" && (
							<MenuItem onClick={handleGoToHelp}>
								<ListItemIcon>
									<ThemedHelpIcon />
								</ListItemIcon>
								Help
							</MenuItem>
						)}

						<MenuItem onClick={handleDropDown}>
							<ListItemIcon>
								<SettingsThemeIcon />
							</ListItemIcon>
							<ListItemText
								primaryTypographyProps={{
									variant: "body3"
								}}
							>
								Theme
							</ListItemText>
							<Typography
								variant="body3"
								color="text.secondary"
								display="flex"
								alignItems={"center"}
							>
								{openSubMenu ? (
									<ThemedAngleLeft
										width="20px"
										height="20px"
									/>
								) : (
									<ThemedAngleRight
										width="20px"
										height="20px"
									/>
								)}
							</Typography>
						</MenuItem>
						<Divider />
					</ProtectedButton>
					{settingsMenuLoading && (
						<MenuItem onClick={handleLogout}>
							<ListItemIcon>
								<SettingsLogoutIcon />
							</ListItemIcon>
							{!isLoggingOut ? (
								"Log out"
							) : (
								<div style={{ display: "flex", alignItems: "center" }}>
									Logging out
									<div className="logout__dots">
										<span />
										<span />
										<span />
									</div>
								</div>
							)}
						</MenuItem>
					)}
				</MenuList>
			</Menu>
			{renderThemeSettingsMenu()}
		</SoftBox>
	);
};

export default SettingsMenu;
